// Saved searches live as JSON files on the work repo's `app-data` branch
// (orphaned from the docs/issues history). Reads go through the GraphQL proxy;
// writes go through /api/saved-searches/commit, which serialises commits to the
// shared branch and retries on a stale head. See n3oltd/work#771.
import { commitAppData, graphql, toBase64 } from './api'
import { SAVED_SEARCHES } from './github-queries'
import { appConfig } from './query'

const BRANCH = 'app-data'
const ROOT = 'saved-searches'

export type ManualPlacement = {
  /** GitHub issue node id. */
  issue: string
  /** Login of whoever placed it — attribution for the visible "flag". */
  by: string
  /** ISO timestamp. */
  at: string
}

/** The on-disk shape of a saved-search file. */
export type SavedSearchFile = {
  name: string
  /** Same syntax `parseFilter` speaks, so files stay portable/hand-editable. */
  filter: string
  createdBy: string
  createdAt: string
  /** Sparse manual order; absent means "automatic order only". */
  order?: ManualPlacement[]
}

export type SavedSearchScope = 'org' | 'user'

/** A loaded saved search with its location resolved. */
export type SavedSearch = SavedSearchFile & {
  scope: SavedSearchScope
  /** Login owning a user-scoped search; null for org. */
  owner: string | null
  /** Filename without `.json`; stable id within its folder. */
  slug: string
}

export type SavedSearchSets = { org: SavedSearch[]; mine: SavedSearch[] }

export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'search'
  )
}

function pathFor(scope: SavedSearchScope, owner: string | null, slug: string): string {
  return scope === 'org' ? `${ROOT}/org/${slug}.json` : `${ROOT}/users/${owner}/${slug}.json`
}

type TreeEntry = { name: string; type: string; object: { text?: string } | null }
type Tree = { entries?: TreeEntry[] } | null

function parseEntries(tree: Tree, scope: SavedSearchScope, owner: string | null): SavedSearch[] {
  const out: SavedSearch[] = []
  for (const entry of tree?.entries ?? []) {
    if (entry.type !== 'blob' || !entry.name.endsWith('.json')) continue
    const text = entry.object?.text
    if (!text) continue
    let file: SavedSearchFile
    try {
      file = JSON.parse(text) as SavedSearchFile
    } catch {
      continue // one malformed file must not blow away the whole list
    }
    if (typeof file.name !== 'string' || typeof file.filter !== 'string') continue
    out.push({ ...file, scope, owner, slug: entry.name.replace(/\.json$/, '') })
  }
  return out
}

/** Org searches (shared) plus the signed-in user's own. Everything is readable
 *  by everyone; the split is attribution, not privacy. */
export async function listSavedSearches(login: string): Promise<SavedSearchSets> {
  const data = await graphql<{ repository: { org: Tree; mine: Tree } }>({
    query: SAVED_SEARCHES,
    variables: {
      owner: appConfig.owner,
      name: appConfig.repo,
      orgExpr: `${BRANCH}:${ROOT}/org`,
      mineExpr: `${BRANCH}:${ROOT}/users/${login}`,
    },
  })
  return {
    org: parseEntries(data.repository?.org ?? null, 'org', null),
    mine: parseEntries(data.repository?.mine ?? null, 'user', login),
  }
}


function label(search: Pick<SavedSearch, 'scope' | 'owner' | 'slug'>): string {
  return `${search.scope === 'org' ? 'org' : search.owner}/${search.slug}`
}

/** Create or overwrite a saved-search file. The write goes through the server,
 *  which serialises commits to the shared branch and retries on a stale head. */
export async function writeSavedSearch(search: SavedSearch): Promise<void> {
  const file: SavedSearchFile = {
    name: search.name,
    filter: search.filter,
    createdBy: search.createdBy,
    createdAt: search.createdAt,
    ...(search.order && search.order.length ? { order: search.order } : {}),
  }
  await commitAppData({
    message: `saved-search: ${label(search)}`,
    additions: [
      {
        path: pathFor(search.scope, search.owner, search.slug),
        contents: toBase64(JSON.stringify(file, null, 2) + '\n'),
      },
    ],
  })
}

/** Rename in place: same slug (the stable id), new display name, every other
 *  field — filter, manual order, attribution — carried through untouched. */
export async function renameSavedSearch(search: SavedSearch, name: string): Promise<void> {
  await writeSavedSearch({ ...search, name })
}

/** Remove a saved-search file. The server enforces who may delete what (your
 *  own user-scoped searches; org searches are shared). */
export async function deleteSavedSearch(search: SavedSearch): Promise<void> {
  await commitAppData({
    message: `saved-search: delete ${label(search)}`,
    deletions: [{ path: pathFor(search.scope, search.owner, search.slug) }],
  })
}
